import * as React from "react";
import { Bar, BarChart, CartesianGrid, LabelList, Rectangle, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Segmented } from "@/components/ui/segmented";
import { prefersReducedMotion } from "@/hooks/use-count-up";
import { euro, mila } from "@/lib/format";

/* «Mese per mese»: il grafico del venduto (pacchetto del 29-10, charts/sold-chart.tsx). Regole della tavola «Grafici»:
   - una serie sola in chart-1; il mese in corso in chart-2, col nome sotto l'asse in grassetto;
   - fino a 12 barre ogni barra porta il suo valore in migliaia e l'asse verticale non c'è;
     oltre 12, valori solo sul massimo e sull'ultimo e l'asse con passi tondi;
   - la linea di riferimento (il passo del milione) è sottile, tratteggiata e ha il suo nome;
   - numero esatto al passaggio e da tastiera; tabella a un tocco con gli stessi numeri;
   - barre larghe al massimo 24 px, punta tonda 4 px; crescono in 650 ms solo al primo disegno.
   SoldChartBody è il grafico senza la scheda, per metterlo dentro un'altra (la Dashboard a 1440). Con
   paceLabel={null} il nome della linea va in una legenda fuori dal grafico, dove non copre le barre. */

function TooltipBox({ active, payload }) {
  const m = active ? payload?.[0]?.payload : undefined;
  if (!m) return null;
  return (
    <div className="rounded-sm bg-toast px-2.5 py-2 text-on-toast shadow-overlay">
      <p className="text-caption font-normal opacity-85">{m.name}</p>
      <p className="font-bold tabular-nums">{euro(m.total)}</p>
      <p className="text-caption font-normal opacity-85">
        {m.contracts} {m.contracts === 1 ? "contratto" : "contratti"}
      </p>
    </div>
  );
}

const migliaia = (n) => String(Math.round(n / 1000));

/** passi tondi per l'asse verticale: 3 intervalli da 10, 20, 40, 50, 100 mila… */
function niceTicks(max) {
  const steps = [10000, 20000, 25000, 40000, 50000, 100000, 200000, 250000, 500000];
  const step = steps.find((s) => s * 3 >= max) ?? Math.ceil(max / 3 / 100000) * 100000;
  return [0, step, step * 2, step * 3];
}

export function SoldChartBody({ months, view = "chart", pace, paceLabel = "passo del milione", anima = true }) {
  // anima solo al primo disegno: dopo un cambio di filtro i dati si aggiornano senza movimento
  const [animate, setAnimate] = React.useState(() => !prefersReducedMotion());
  React.useEffect(() => {
    const t = setTimeout(() => setAnimate(false), 700);
    return () => clearTimeout(t);
  }, []);

  const max = months.length ? Math.max(...months.map((m) => m.total)) : 0;
  const pochi = months.length <= 12;
  const ticks = niceTicks(Math.max(max, pace ?? 0, 1));
  const top = ticks[ticks.length - 1];

  if (max <= 0) return <p className="py-10 text-center text-ink-2">Ancora nessun contratto firmato in questo periodo.</p>;

  if (view === "table")
    return (
      <table className="w-full border-collapse tabular-nums" data-testid="grafico-tabella">
        <thead>
          <tr className="text-left text-small text-ink-2">
            <th scope="col" className="py-2 font-semibold">Mese</th>
            <th scope="col" className="py-2 text-right font-semibold">Venduto firmato</th>
            <th scope="col" className="py-2 text-right font-semibold">Contratti</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.short + m.name} className={m.current ? "font-bold" : undefined}>
              <td className="border-t border-line-subtle py-1.5">{m.name}</td>
              <td className="border-t border-line-subtle py-1.5 text-right">{euro(m.total)}</td>
              <td className="border-t border-line-subtle py-1.5 text-right">{m.contracts}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

  return (
    <div className="h-[240px]" data-testid="grafico-mesi">
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 240 }}>
        <BarChart data={months} margin={{ top: 24, right: 4, bottom: 0, left: 0 }} barCategoryGap={6} accessibilityLayer>
          {!pochi && <CartesianGrid vertical={false} stroke="var(--border-subtle)" />}
          <XAxis
            dataKey="short"
            tickLine={false}
            axisLine={{ stroke: "var(--border-chip)" }}
            interval={0}
            tick={(p) => {
              const m = months[p.payload?.index ?? -1];
              return (
                <text x={Number(p.x)} y={Number(p.y) + 12} textAnchor="middle" fontSize={12} fill={m?.current ? "var(--text-strong)" : "var(--text-3)"} fontWeight={m?.current ? 700 : 500}>
                  {p.payload?.value}
                </text>
              );
            }}
          />
          <YAxis hide={pochi} width={64} domain={[0, top]} ticks={ticks} tickFormatter={(v) => mila(v)} tickLine={false} axisLine={false} tick={{ fill: "var(--text-3)", fontSize: 12 }} />
          {pace !== undefined && (
            <ReferenceLine
              y={pace}
              stroke="var(--text-3)"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              label={paceLabel ? { value: paceLabel, position: "insideTopLeft", fill: "var(--text-2)", fontSize: 12 } : undefined}
            />
          )}
          <Tooltip cursor={{ fill: "var(--surface-2)" }} content={(p) => <TooltipBox active={p.active} payload={p.payload} />} />
          <Bar
            dataKey="total"
            maxBarSize={24}
            isAnimationActive={animate && anima}
            animationDuration={650}
            animationEasing="ease-out"
            shape={(p) => <Rectangle {...p} radius={[4, 4, 0, 0]} fill={p.payload?.current ? "var(--chart-2)" : "var(--chart-1)"} />}
          >
            <LabelList
              dataKey="total"
              position="top"
              content={(p) => {
                const i = Number(p.index);
                const m = months[i];
                if (!m || !m.total) return null;
                if (!pochi && !(m.total === max || i === months.length - 1)) return null;
                return (
                  <text x={Number(p.x) + Number(p.width) / 2} y={Number(p.y) - 4} textAnchor="middle" fill="var(--text-strong)" fontSize={12} fontWeight={700}>
                    {pochi ? migliaia(m.total) : mila(m.total)}
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function SoldChart({ months, title = "Quanto abbiamo venduto", subtitle = "Mese per mese, in migliaia di euro", pace, paceLabel }) {
  const titleId = React.useId();
  const [view, setView] = React.useState("chart");
  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-5 md:px-7 md:py-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 id={titleId} className="font-display text-card-title text-ink-strong">{title}</h2>
          {subtitle && <p className="text-ink-2">{subtitle}</p>}
        </div>
        <Segmented
          aria-label="Mostra come"
          size="sm"
          value={view}
          onValueChange={setView}
          options={[
            { value: "chart", label: "Grafico" },
            { value: "table", label: "Tabella" },
          ]}
        />
      </header>
      <SoldChartBody months={months} view={view} pace={pace} paceLabel={paceLabel} />
    </section>
  );
}
